import React, { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { claimShaderComposerFocus, subscribeShaderComposerFocus } from './composer-focus'
import { ShaderGenerationStatus } from './ShaderGenerationStatus'
import { ShaderInputBadges } from './ShaderInputChips'
import type { GenerationFailure } from './generation-state'
import type { ShaderGenerationProgress } from '@/lib/canvas/shader/authoring/types'
import type { ShaderInputType } from '@/lib/canvas/shader/media/contract'

export interface ShaderPromptComposerProps {
  nodeId: string
  /** The saved draft (`state.prompt`). */
  initialPrompt: string
  hasCode: boolean
  connectedInputs: readonly ShaderInputType[]
  running: boolean
  progress: ShaderGenerationProgress | null
  failure: GenerationFailure | null
  /** The last generation's one-line summary. */
  summary?: string
  /** Increments after each successful generation; growth while mounted clears the box. */
  completed: number
  /** Who writes the shader and, for a local model, where the prompt is sent. */
  providerLabel?: string
  onSubmit: (prompt: string) => void
  onCancel: () => void
  /** Saves the draft to the node when the box loses focus. */
  onDraftCommit: (prompt: string) => void
}

/**
 * The prompt box on a Shader node's face. An empty node creates a shader from
 * it; a node with code sends it as an edit to what is running.
 */
export function ShaderPromptComposer({
  nodeId,
  initialPrompt,
  hasCode,
  connectedInputs,
  running,
  progress,
  failure,
  summary,
  completed,
  providerLabel,
  onSubmit,
  onCancel,
  onDraftCommit,
}: ShaderPromptComposerProps) {
  const [draft, setDraft] = useState(initialPrompt)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const committed = useRef(initialPrompt)

  // A draft saved elsewhere (another view of the node, an undo) replaces this one.
  useEffect(() => {
    committed.current = initialPrompt
    setDraft(initialPrompt)
  }, [initialPrompt])

  // The count outlives the node (it unmounts off-screen); only a success seen while mounted clears the box,
  // so a remount keeps the saved draft.
  const seenCompleted = useRef(completed)
  useEffect(() => {
    if (completed > seenCompleted.current) setDraft('')
    seenCompleted.current = completed
  }, [completed])

  useEffect(() => {
    const focus = () => requestAnimationFrame(() => textareaRef.current?.focus())
    if (claimShaderComposerFocus(nodeId)) focus()
    return subscribeShaderComposerFocus(nodeId, focus)
  }, [nodeId])

  const submit = useCallback(() => {
    if (running || !draft.trim()) return
    onSubmit(draft)
  }, [running, draft, onSubmit])

  const handleSubmitClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    submit()
  }, [submit])

  const handleCancelClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onCancel()
  }, [onCancel])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Typing belongs to the box: keep Backspace/Delete and letter shortcuts off the canvas.
    if (!e.metaKey && !e.ctrlKey) e.stopPropagation()
    if (e.key === 'Escape') {
      e.currentTarget.blur()
      return
    }
    // Safari ends composition before the Enter that confirms an IME candidate; that keydown reports keyCode 229.
    const composing = e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229
    if (e.key === 'Enter' && !e.shiftKey && !composing) {
      e.preventDefault()
      submit()
    }
  }, [submit])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value), [])

  const handleBlur = useCallback(() => {
    if (draft === committed.current) return
    committed.current = draft
    onDraftCommit(draft)
  }, [draft, onDraftCommit])

  const stop = useCallback((e: React.SyntheticEvent) => e.stopPropagation(), [])

  return (
    <div className="flex flex-col gap-1.5">
      <ShaderInputBadges inputs={connectedInputs} />
      <div
        className={cn(
          'flex items-end gap-1.5 rounded-md border border-border bg-background/60 px-2 py-1.5 focus-within:border-primary/60',
          running && 'opacity-80',
        )}
      >
        <Icon name="sparkles" className="mt-px h-3.5 w-3.5 shrink-0 self-start text-muted-foreground" />
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onDoubleClick={stop}
          // Read-only rather than disabled while running, so focus (and the draft) stay put.
          readOnly={running}
          rows={2}
          spellCheck={false}
          aria-label={hasCode ? 'Describe a change' : 'Describe a shader'}
          placeholder={hasCode ? 'Describe a change...' : 'Describe a shader...'}
          className="nodrag nowheel nopan max-h-32 min-h-[2.25rem] flex-1 resize-none bg-transparent text-xs leading-snug outline-none placeholder:text-muted-foreground read-only:cursor-default"
        />
        {running ? (
          <Button variant="outline" size="sm" onClick={handleCancelClick} className="h-7 shrink-0 rounded-md px-2.5 text-[11px]" aria-label="Cancel generation">
            Cancel
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={handleSubmitClick}
            disabled={!draft.trim()}
            className="h-7 w-7 shrink-0 rounded-md p-0"
            aria-label={hasCode ? 'Apply change' : 'Write shader'}
            title="Enter to send, Shift+Enter for a new line"
          >
            <Icon name="arrow-up" className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {providerLabel && (
        <p className="truncate text-[10px] text-muted-foreground" title={providerLabel} data-testid="shader-provider-line">
          {providerLabel}
        </p>
      )}
      <ShaderGenerationStatus running={running} progress={progress} failure={failure} summary={summary} />
    </div>
  )
}
